/**
 * Tax Returns — country-aware summary with drill-down + downloadable JSON.
 * Auto-selects the return type based on tenant country.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Download, FileText } from 'lucide-react';
import { booksClient, getBooksTenant, errMsg } from './api';
import { toast } from '../hooks/use-toast';

const RETURN_TYPE = {
  GB: { path: 'uk-vat', label: 'UK VAT — 9-Box HMRC Return' },
  IN: { path: 'in-gst', label: 'India GSTR-1 & GSTR-3B' },
  MY: { path: 'my-sst', label: 'Malaysia SST Return' },
  KZ: { path: 'kz-vat', label: 'Kazakhstan VAT Return' },
};

const TaxReturnsPage = () => {
  const tenant = getBooksTenant();
  const rt = RETURN_TYPE[tenant?.country_code] || RETURN_TYPE.GB;
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(lastOfMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await booksClient.get(`/tax-returns/${rt.path}`, { params: { from_date: fromDate, to_date: toDate } });
      setData(r.data);
    } catch (e) {
      toast({ title: errMsg(e), variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { run(); }, []); // eslint-disable-line

  const downloadJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${rt.path}-${fromDate}-to-${toDate}.json`;
    link.click();
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">Compliance</div>
          <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{rt.label}</h1>
          <div className="text-sm text-[#0b1a2e]/60 mt-1">Auto-summarised from every posted journal in the selected period.</div>
        </div>
        {data && (
          <button onClick={downloadJson} data-testid="tax-download"
            className="inline-flex items-center gap-1 px-4 py-2 border border-[#0f5c3d] text-[#0f5c3d] rounded-full text-sm font-semibold">
            <Download className="w-3.5 h-3.5" /> Download JSON
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-black/5 p-4 mb-6 flex items-end gap-3 flex-wrap">
        <L label="From"><input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="tax-from" className="px-3 py-2 border border-slate-300 rounded text-sm" /></L>
        <L label="To"><input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="tax-to" className="px-3 py-2 border border-slate-300 rounded text-sm" /></L>
        <button onClick={run} data-testid="tax-run" className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">Recalculate</button>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
        !data ? null :
        tenant.country_code === 'GB' ? <UKVATView data={data} /> :
        tenant.country_code === 'IN' ? <INGSTView data={data} /> :
        tenant.country_code === 'MY' ? <MYSSTView data={data} /> :
        <KZVATView data={data} />
      }
    </div>
  );
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const UKVATView = ({ data }) => {
  const b = data.boxes;
  const boxes = [
    { key: 'box_1_vat_due_on_sales', label: 'Box 1 — VAT due on sales', value: b.box_1_vat_due_on_sales },
    { key: 'box_2_vat_due_on_eu_acquisitions', label: 'Box 2 — VAT due on EU acquisitions', value: b.box_2_vat_due_on_eu_acquisitions },
    { key: 'box_3_total_vat_due', label: 'Box 3 — Total VAT due (1+2)', value: b.box_3_total_vat_due, highlight: true },
    { key: 'box_4_vat_reclaimable', label: 'Box 4 — VAT reclaimable', value: b.box_4_vat_reclaimable },
    { key: 'box_5_net_vat_payable', label: 'Box 5 — Net VAT payable to HMRC', value: b.box_5_net_vat_payable, highlight: true, big: true },
    { key: 'box_6_total_sales_ex_vat', label: 'Box 6 — Total sales ex VAT', value: b.box_6_total_sales_ex_vat },
    { key: 'box_7_total_purchases_ex_vat', label: 'Box 7 — Total purchases ex VAT', value: b.box_7_total_purchases_ex_vat },
    { key: 'box_8_eu_dispatches', label: 'Box 8 — EU dispatches', value: b.box_8_eu_dispatches },
    { key: 'box_9_eu_acquisitions', label: 'Box 9 — EU acquisitions', value: b.box_9_eu_acquisitions },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {boxes.map((x) => (
          <div key={x.key} data-testid={`tax-${x.key}`}
            className={`bg-white p-4 rounded-xl border ${x.highlight ? 'border-[#0f5c3d] bg-[#f7fbf8]' : 'border-black/5'}`}>
            <div className="text-[10px] font-semibold tracking-widest uppercase text-[#0b1a2e]/60">{x.label}</div>
            <div className={`mt-1 font-mono font-semibold ${x.big ? 'text-2xl text-[#0f5c3d]' : 'text-lg text-[#0b1a2e]'}`}>{fmt(x.value)}</div>
          </div>
        ))}
      </div>
      <BreakdownTable rows={data.tax_lines_breakdown} />
    </div>
  );
};

const INGSTView = ({ data }) => {
  const g1 = data.gstr_1_outward, g3 = data.gstr_3b_summary;
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-black/5 p-5">
        <h2 className="text-xl mb-3 text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>GSTR-1 — Outward Supplies</h2>
        <div className="grid grid-cols-3 gap-3">
          <StatCard label="Intra-state" value={g1.intra_state.taxable_value} sub={`CGST+SGST ${fmt(g1.intra_state.tax)}`} />
          <StatCard label="Inter-state" value={g1.inter_state.taxable_value} sub={`IGST ${fmt(g1.inter_state.tax)}`} />
          <StatCard label="Zero-rated" value={g1.zero_rated.taxable_value} sub="Nil" />
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-black/5 p-5">
        <h2 className="text-xl mb-3 text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>GSTR-3B — Summary</h2>
        <div className="grid grid-cols-2 gap-4">
          <div><div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Output tax</div>
            <div className="font-mono text-2xl text-[#0b1a2e] mt-1">{fmt(g3.output_tax.total)}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">Input tax credit</div>
            <div className="font-mono text-2xl text-[#0b1a2e] mt-1">{fmt(g3.input_tax_credit_available.total)}</div></div>
        </div>
        <div className="mt-3 border-t pt-3 flex justify-between items-center">
          <div className="text-sm font-semibold text-[#0b1a2e]">Net Payable</div>
          <div className="font-mono text-2xl text-[#0f5c3d] font-semibold">{fmt(g3.net_payable)}</div>
        </div>
      </div>
      <BreakdownTable rows={data.gstr_1_outward.lines} />
    </div>
  );
};

const MYSSTView = ({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 gap-3">
      <StatCard label="Sales Tax 10%" value={data.sales_tax_10pct.taxable_value} sub={`Tax ${fmt(data.sales_tax_10pct.tax)}`} />
      <StatCard label="Service Tax 8%" value={data.service_tax_8pct.taxable_value} sub={`Tax ${fmt(data.service_tax_8pct.tax)}`} />
    </div>
    <div className="bg-[#f7fbf8] rounded-2xl border border-[#0f5c3d]/30 p-5 flex justify-between items-center">
      <div className="text-sm font-semibold text-[#0b1a2e]">Total SST Payable</div>
      <div className="font-mono text-3xl text-[#0f5c3d] font-semibold">{fmt(data.total_payable)}</div>
    </div>
    <BreakdownTable rows={data.lines} />
  </div>
);

const KZVATView = ({ data }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-3 gap-3">
      <StatCard label="Output VAT 12%" value={data.output_vat_12pct} />
      <StatCard label="Input VAT" value={data.input_vat} />
      <StatCard label="Net Payable" value={data.net_payable} highlight />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <StatCard label="Total Revenue" value={data.total_revenue} />
      <StatCard label="Total Expenses" value={data.total_expenses} />
    </div>
    <BreakdownTable rows={data.lines_breakdown} />
  </div>
);

const StatCard = ({ label, value, sub, highlight }) => (
  <div className={`bg-white p-4 rounded-xl border ${highlight ? 'border-[#0f5c3d] bg-[#f7fbf8]' : 'border-black/5'}`}>
    <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">{label}</div>
    <div className={`mt-1 font-mono font-semibold ${highlight ? 'text-2xl text-[#0f5c3d]' : 'text-xl text-[#0b1a2e]'}`}>{fmt(value)}</div>
    {sub && <div className="text-[10px] mt-1 text-[#0b1a2e]/50">{sub}</div>}
  </div>
);

const BreakdownTable = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60">By tax code</div>
      <table className="w-full text-sm">
        <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
          <tr>
            <th className="text-left px-4 py-2">Kind</th>
            <th className="text-left px-4 py-2">Code</th>
            <th className="text-right px-4 py-2">Rate</th>
            <th className="text-right px-4 py-2">Taxable</th>
            <th className="text-right px-4 py-2">Tax</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="px-4 py-2 text-xs uppercase tracking-widest text-[#0b1a2e]/70">{r.kind}</td>
              <td className="px-4 py-2 font-mono text-xs">{r.tax_code}</td>
              <td className="px-4 py-2 text-right font-mono">{r.rate || 0}%</td>
              <td className="px-4 py-2 text-right font-mono">{fmt(r.taxable_amount)}</td>
              <td className="px-4 py-2 text-right font-mono">{fmt(r.tax_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const L = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default TaxReturnsPage;
